import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquarePlus, LogOut, Database, Brain, Zap, PhoneOutgoing, Factory, BookOpen, Type, ExternalLink, ChevronDown, CheckCircle2, PauseCircle, FlaskConical, Rocket } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { hasAccess } from '../utils/access';
import manifestData from '../config/manifest.json';

// Apps derived from manifest — no separate apps.js needed
const apps = manifestData.pages
  .filter(p => p.contentType === 'app' || p.tab === 'both')
  .map(p => ({
    id: p.slug,
    title: p.title,
    description: p.description,
    icon: p.icon,
    url: p.appUrl,
    iconBg: p.iconBg,
    iconColor: p.iconColor,
    borderAccent: p.borderAccent || '#6B7280',
    requiredGroups: p.requiredGroups || [],
    tags: p.appTags || p.tags || [],
    status: 'live',
  }));

// Map icon name strings from manifest to Lucide components
const iconMap = {
  Factory, BookOpen, Zap, PhoneOutgoing, FlaskConical, Database,
  Type, MessageSquarePlus, Brain, Rocket,
};

function HomePage() {
  const { user, groups, login, logout, authError } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('tools');
  const [archiveOpen, setArchiveOpen] = useState(false);

  const handleLogin = async () => {
    try {
      await login();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleLogout = async () => {
    try {
      // Clear Salesforce token
      localStorage.removeItem('sf_auth_token');
      // Logout from Google
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Tile data driven by manifest.json — no code changes needed to add/remove pages
  const wizardOptions = manifestData.pages.map(page => ({
    id: page.slug,
    title: page.title,
    description: page.description,
    icon: iconMap[page.icon] || Database,
    path: page.contentType === 'app' ? null : page.route,
    appUrl: page.appUrl || null,
    iconBg: page.iconBg || 'bg-gray-50',
    iconColor: page.iconColor || 'text-gray-700',
    status: page.status || 'active',
    tags: page.tags || [],
    contentType: page.contentType || 'static',
  }));

  const activeTools = wizardOptions.filter((w) => w.status === 'active' && w.contentType !== 'app');
  const archivedTools = wizardOptions.filter((w) => (w.status === 'completed' || w.status === 'deprioritized') && w.contentType !== 'app');

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5EFE8' }}>

      {/* Top bar */}
      <div className="flex justify-between items-center px-6 py-3 bg-white/80 backdrop-blur-sm border-b border-stone-200">
        <span className="text-sm font-medium" style={{ color: '#5B9E9B' }}>Innovation Lab</span>
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-4" style={{ opacity: 0.45 }}>
            <a href="https://cloudcompliance.app" target="_blank" rel="noopener noreferrer">
              <img src="/logo-cc.webp" alt="Cloud Compliance" className="h-4 w-auto hover:opacity-70 transition-opacity" />
            </a>
            <div className="w-px h-3" style={{ backgroundColor: '#C4956A' }} />
            <a href="https://gptfy.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:opacity-70 transition-opacity">
              <img src="/logo-gptfy.png" alt="GPTfy" className="h-4 w-auto" />
              <span className="text-xs font-medium tracking-wide" style={{ color: '#2A3540' }}>GPTfy</span>
            </a>
          </div>
          {user && (
            <button onClick={handleLogout} className="flex items-center gap-1.5 text-sm hover:opacity-70 transition-opacity" style={{ color: '#546E7A' }}>
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          )}
        </div>
      </div>

      {/* Hero — image blending into cream background */}
      <div className="relative w-full" style={{ height: '420px' }}>
        <img src="/hero.webp" alt="" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 35%, #F5EFE8 100%)' }} />
        <div className="absolute bottom-0 left-0 right-0 pb-10 text-center px-6">
          <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#5B9E9B' }}>Cloud Compliance · GPTfy</p>
          <h1 className="text-5xl mb-3" style={{ fontFamily: '"DM Serif Display", Georgia, serif', color: '#2A3540' }}>Innovation Lab</h1>
          <p className="text-base max-w-sm mx-auto leading-relaxed" style={{ color: '#546E7A' }}>
            Where tomorrow's features take shape — before they ship.
          </p>
        </div>
      </div>

      {!user ? (
        <div className="text-center py-12">
          {authError && <p className="mb-3 text-sm" style={{ color: '#B85C3A' }}>{authError}</p>}
          <button
            onClick={handleLogin}
            className="px-8 py-3 rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#2A3540' }}
          >
            Sign in with Google
          </button>
        </div>
      ) : (
        <div className="px-6 pb-20 -mt-2">
          {/* Tab bar — only show when user has visible apps */}
          {(() => {
            const visibleApps = apps.filter((app) => hasAccess(groups, app.requiredGroups));
            const showTabs = visibleApps.length > 0;
            return showTabs ? (
              <div className="max-w-4xl mx-auto mb-6">
                <div className="flex gap-6 border-b border-stone-200">
                  {['tools', 'apps'].map((tab) => {
                    const isActive = activeTab === tab;
                    const label = tab === 'tools' ? 'Tools' : `Apps (${visibleApps.length})`;
                    return (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className="pb-2.5 text-sm font-medium transition-colors relative"
                        style={{
                          color: isActive ? '#2A3540' : '#9E9287',
                          borderBottom: isActive ? '2px solid #5B9E9B' : '2px solid transparent',
                          marginBottom: '-1px',
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null;
          })()}

          {/* Tools tab */}
          {activeTab === 'tools' && (
            <div className="max-w-4xl mx-auto">
              {/* Active tools grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:[grid-auto-rows:1fr]">
                {activeTools.map((wizard) => (
                  <div
                    key={wizard.id}
                    className="block h-full cursor-pointer"
                    onClick={() => { if (wizard.path) { navigate(wizard.path); } else if (wizard.appUrl) { window.open(wizard.appUrl, '_blank'); } }}
                    role="link"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') { if (wizard.path) { navigate(wizard.path); } else if (wizard.appUrl) { window.open(wizard.appUrl, '_blank'); } } }}
                  >
                    <div className="h-full bg-white rounded-xl border border-stone-200 p-6 hover:shadow-lg hover:shadow-stone-200/60 hover:-translate-y-0.5 transition-all flex flex-col gap-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${wizard.iconBg}`}>
                          {React.createElement(wizard.icon, { className: `w-5 h-5 ${wizard.iconColor}` })}
                        </div>
                        <h2 className="font-semibold text-base leading-snug flex-grow" style={{ color: '#2A3540' }}>
                          {wizard.title}
                        </h2>
                        {wizard.appUrl && (
                          <a
                            href={wizard.appUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full hover:opacity-80 transition-opacity flex-shrink-0"
                            style={{ backgroundColor: '#F0FDFA', color: '#0D9488', border: '1px solid #CCFBF1' }}
                            title="Launch app"
                          >
                            <Rocket className="w-3 h-3" />
                            Launch
                          </a>
                        )}
                      </div>
                      <p className="text-sm leading-relaxed flex-grow" style={{ color: '#546E7A' }}>
                        {wizard.description}
                      </p>
                      {wizard.tags && (
                        <div className="flex flex-wrap gap-1.5 pt-1 border-t border-stone-100">
                          {wizard.tags.map(tag => (
                            <span
                              key={tag.name}
                              className="text-xs font-medium px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: tag.bg, color: tag.color }}
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Completed & Deprioritized accordion */}
              {archivedTools.length > 0 && (
                <div className="mt-8">
                  <button
                    onClick={() => setArchiveOpen(!archiveOpen)}
                    className="flex items-center gap-2 text-sm font-medium transition-colors hover:opacity-70 mb-4"
                    style={{ color: '#9E9287' }}
                  >
                    <ChevronDown
                      className="w-4 h-4 transition-transform"
                      style={{ transform: archiveOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    />
                    Completed & Deprioritized ({archivedTools.length})
                  </button>
                  {archiveOpen && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:[grid-auto-rows:1fr]">
                      {archivedTools.map((wizard) => (
                        <Link key={wizard.id} to={wizard.path || '#'} className="block h-full">
                          <div className="h-full bg-white rounded-xl border border-stone-200 p-6 flex flex-col gap-3 transition-all hover:shadow-sm" style={{ opacity: 0.65 }}>
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${wizard.iconBg}`}>
                                {React.createElement(wizard.icon, { className: `w-5 h-5 ${wizard.iconColor}` })}
                              </div>
                              <h2 className="font-semibold text-base leading-snug flex-grow" style={{ color: '#2A3540' }}>
                                {wizard.title}
                              </h2>
                              {wizard.status === 'completed' && (
                                <span className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F0FDF4', color: '#166534' }}>
                                  <CheckCircle2 className="w-3 h-3" />
                                  Done
                                </span>
                              )}
                              {wizard.status === 'deprioritized' && (
                                <span className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F5F5F4', color: '#9E9287' }}>
                                  <PauseCircle className="w-3 h-3" />
                                  Paused
                                </span>
                              )}
                            </div>
                            <p className="text-sm leading-relaxed flex-grow" style={{ color: '#546E7A' }}>
                              {wizard.description}
                            </p>
                            {wizard.tags && (
                              <div className="flex flex-wrap gap-1.5 pt-1 border-t border-stone-100">
                                {wizard.tags.map(tag => (
                                  <span
                                    key={tag.name}
                                    className="text-xs font-medium px-2 py-0.5 rounded-full"
                                    style={{ backgroundColor: tag.bg, color: tag.color }}
                                  >
                                    {tag.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Apps tab */}
          {activeTab === 'apps' && (
            <div className="max-w-4xl mx-auto">
              {(() => {
                const visibleApps = apps.filter((app) => hasAccess(groups, app.requiredGroups));
                if (visibleApps.length === 0) {
                  return (
                    <div className="text-center py-16">
                      <p className="text-sm" style={{ color: '#9E9287' }}>No applications available for your account.</p>
                    </div>
                  );
                }
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:[grid-auto-rows:1fr]">
                    {visibleApps.map((app) => {
                      const isLive = app.status === 'live';
                      const card = (
                        <div
                          className="h-full bg-white rounded-xl border border-stone-200 p-6 flex flex-col gap-3 transition-all"
                          style={{
                            borderLeft: `3px solid ${app.borderAccent}`,
                            opacity: isLive ? 1 : 0.55,
                            cursor: isLive ? 'pointer' : 'default',
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${app.iconBg}`}>
                              {React.createElement(iconMap[app.icon] || Database, { className: `w-5 h-5 ${app.iconColor}` })}
                            </div>
                            <h2 className="font-semibold text-base leading-snug flex-grow" style={{ color: '#2A3540' }}>
                              {app.title}
                            </h2>
                            {isLive && <ExternalLink className="w-4 h-4 flex-shrink-0" style={{ color: '#9E9287' }} />}
                            {!isLive && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F5F5F4', color: '#9E9287' }}>
                                Coming Soon
                              </span>
                            )}
                          </div>
                          <p className="text-sm leading-relaxed flex-grow" style={{ color: '#546E7A' }}>
                            {app.description}
                          </p>
                          {app.tags && (
                            <div className="flex flex-wrap gap-1.5 pt-1 border-t border-stone-100">
                              {app.tags.map(tag => (
                                <span
                                  key={tag.name}
                                  className="text-xs font-medium px-2 py-0.5 rounded-full"
                                  style={{ backgroundColor: tag.bg, color: tag.color }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                      if (isLive) {
                        return (
                          <a key={app.id} href={app.url} target="_blank" rel="noopener noreferrer" className="block h-full hover:shadow-lg hover:shadow-stone-200/60 hover:-translate-y-0.5 transition-all rounded-xl">
                            {card}
                          </a>
                        );
                      }
                      return <div key={app.id} className="block h-full">{card}</div>;
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Brand footer */}
          <div className="max-w-3xl mx-auto mt-12 mb-2 flex flex-col items-center gap-3" style={{ opacity: 0.4 }}>
            <div className="w-16 h-px" style={{ backgroundColor: '#C4956A' }} />
            <div className="flex items-center gap-6">
              <img src="/logo-cc.webp" alt="Cloud Compliance" className="h-6 w-auto" />
              <div className="w-px h-5" style={{ backgroundColor: '#2A3540' }} />
              <div className="flex items-center gap-2">
                <img src="/logo-gptfy.png" alt="GPTfy" className="h-6 w-auto" />
                <span className="text-xs tracking-widest uppercase" style={{ color: '#2A3540', letterSpacing: '0.1em' }}>GPTfy</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copyright — always visible */}
      <div className="pb-8 text-center">
        <p className="text-xs" style={{ color: '#9E9287' }}>
          © {new Date().getFullYear()} PlumCloud Labs, LLC. · All rights reserved. · For internal use only.
        </p>
      </div>
    </div>
  );
}

export default HomePage;
